import { useCallback, useEffect, useRef, useState } from "react";

import { ErrorDelEdge } from "./edge";
import {
  REFRESCO_MS,
  colorDe,
  guardarSesion,
  guardarPase,
  paseGuardado,
  letreroDe,
  ligaDeLaUrl,
  olvidarSesion,
  puedeMostrarQR,
  saldoComoTexto,
  sesionActual as sesionGuardada,
  type PaseDelColaborador,
  type PuertoDelPase,
} from "./pase-modelo";

/**
 * PI Barra — el pase del colaborador, en su propio teléfono.
 *
 * Abre una URL con su liga, se identifica, y ve **su** QR del día con su saldo.
 * Sin instalar nada.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * NO HAY BOTÓN DE ACTIVAR, Y NO ES UN OLVIDO
 * ═══════════════════════════════════════════════════════════════════════════
 * El laboratorio del carril D pinta un botón «Activar pase», y el levantamiento
 * midió que **no tiene ruta que el colaborador pueda llamar**: lo que ese botón
 * hace son dos POST, `asignaciones` y `credenciales`, y los dos son
 * `require_admin`. O sea que esa pantalla promete algo que el contrato niega.
 *
 * Manda la decisión del 9-sep: **gerencia activa el pase, el colaborador solo lo
 * ve.** Así que cuando no hay pase, esta pantalla lo DICE y dice quién lo puede
 * arreglar, en vez de ofrecer un botón que va a contestar 403.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ES DE UNA PERSONA, EN UN TELÉFONO QUE PUEDE ESTAR PRESTADO
 * ═══════════════════════════════════════════════════════════════════════════
 * De ahí tres decisiones que en el panel de la barra no harían falta:
 *
 *   · la sesión vive en su propia llave (`tap:pase`), no en la de `edge.ts`, para
 *     que no pise la del bartender si las dos apps comparten origen.
 *   · hay «Salir», y borra la sesión. Un pase sin forma de cerrarse en un
 *     teléfono prestado es el saldo de alguien a la vista del siguiente.
 *   · la liga se consume de la URL y **se limpia de la barra de direcciones** en
 *     cuanto se usa, para que no quede en el historial ni se comparta por error.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * EL QR LLEGA POR `puerto.dibujarQR`, NO POR UN IMPORT
 * ═══════════════════════════════════════════════════════════════════════════
 * `shared/panel` no tiene `node_modules` encima: hoy su único import desnudo es
 * `react`, y hasta ese necesitó `paths` en los dos tsconfig. `qrcode` está en
 * `kiosk` y en el laboratorio pero NO en `kds`. Quien monta la pantalla ya tiene
 * su librería; que la pase.
 */
export function Pase({
  puerto,
  urlInicial,
  alLimpiarUrl,
}: {
  puerto: PuertoDelPase;
  /** La URL con la que se abrió la app. De aquí sale la liga. */
  urlInicial: string;
  /** Cómo quitar la liga de la barra de direcciones. Lo decide quien monta. */
  alLimpiarUrl?: () => void;
}) {
  const [sesion, setSesion] = useState<string | null>(() => sesionGuardada());
  const [pase, setPase] = useState<PaseDelColaborador | null>(null);
  const [svgQR, setSvgQR] = useState<string>("");
  const [cargando, setCargando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);
  const [sinRed, setSinRed] = useState(false);
  const [walletOcupado, setWalletOcupado] = useState(false);
  const [walletMensaje, setWalletMensaje] = useState('');
  const identidad = useRef(0);
  const sesionVigente = useRef(sesion);
  const [entradaPendiente, setEntradaPendiente] = useState(false);
  const [nombre, setNombre] = useState<string>("");

  // Para no pedir el pase sobre una sesión que ya se cerró.
  const vivo = useRef(true);
  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  /* ── La entrada con la liga ─────────────────────────────────────────────── */

  const entrar = useCallback(
    async (token: string) => {
      const revision = ++identidad.current;
      setEntradaPendiente(true);
      sesionVigente.current = null;
      setCargando(true);
      try {
        const s = await puerto.entrar(token);
        if (!vivo.current || revision !== identidad.current) return;
        olvidarSesion(); setPase(null); setSvgQR(''); setNombre(''); setSinRed(false); setWalletMensaje(''); setWalletOcupado(false);
        sesionVigente.current = s.token;
        guardarSesion(s.token);
        identidad.current++;
        setSesion(s.token);
        setNombre(s.nombre);
        setEntradaPendiente(false); setCargando(false);
        setProblema(null);
        // La liga ya se gastó: fuera de la barra de direcciones. Si se queda,
        // recargar intenta canjearla otra vez y el servidor contesta —con
        // razón— que ya se usó, y la persona ve un error sobre algo que hizo
        // bien.
        alLimpiarUrl?.();
      } catch (e) {
        if (!vivo.current || revision !== identidad.current) return;
        sesionVigente.current = sesionGuardada();
        setEntradaPendiente(false);
        setProblema(e instanceof Error ? e.message : String(e));
      } finally {
        if (vivo.current && revision === identidad.current) setCargando(false);
      }
    },
    [puerto, alLimpiarUrl],
  );

  const ultimaLiga = useRef('');
  useEffect(() => {
    const procesar = (url: string) => {
      const liga = ligaDeLaUrl(url);
      if (!liga || liga === ultimaLiga.current) return;
      ultimaLiga.current = liga;
      void entrar(liga);
    };
    procesar(urlInicial);
    const cambio = () => procesar(window.location.href);
    window.addEventListener('hashchange', cambio);
    window.addEventListener('popstate', cambio);
    return () => { window.removeEventListener('hashchange', cambio); window.removeEventListener('popstate', cambio); };
  }, [urlInicial, entrar]);

  /* ── El pase ────────────────────────────────────────────────────────────── */

  const traer = useCallback(async () => {
    // Un efecto del render anterior puede arrancar después de abrir una liga.
    // Su 401 no debe cancelar la entrada nueva que aún está en vuelo.
    if (entradaPendiente || !sesion || sesion !== sesionVigente.current) return;
    const revision = identidad.current;
    setSinRed(true);
    try {
      const p = await puerto.miPase();
      if (!vivo.current || revision !== identidad.current) return;
      guardarPase(p, sesion); setSinRed(false);
      setNombre(p.nombre ?? "");
      setPase(p);
      setProblema(null);
      // El QR se dibuja solo si hay algo que dibujar. Un SVG viejo en pantalla
      // sobre un pase que ya no está disponible es peor que ninguno.
      if (puedeMostrarQR(p) && p.token) {
        const svg = await puerto.dibujarQR(p.token);
        if (revision === identidad.current) setSvgQR(svg);
      } else {
        setSvgQR("");
      }
    } catch (e) {
      if (!vivo.current || revision !== identidad.current) return;
      // 401 o 403: la sesión murió o gerencia le cerró la puerta. Se borra y se
      // pinta la pantalla de la liga, con el motivo que dio el servidor.
      if (e instanceof ErrorDelEdge && e.esDePermiso) {
        identidad.current++;
        sesionVigente.current = null;
        olvidarSesion();
        setSesion(null);
        setPase(null);
        setSvgQR("");
      }
      if (!(e instanceof ErrorDelEdge && e.esDePermiso)) {
        const guardado = paseGuardado(sesion);
        setSinRed(true);
        if (guardado?.token) {
          setPase(guardado); setNombre(guardado.nombre ?? '');
          const svg = await puerto.dibujarQR(guardado.token);
          if (revision === identidad.current) setSvgQR(svg);
        } else { setSvgQR(''); }
      } else { setSvgQR(''); }
      setProblema(e instanceof Error ? e.message : String(e));
    }
  }, [puerto, sesion, entradaPendiente]);

  useEffect(() => {
    if (!sesion) return;
    void traer();
    // Cada 15 s se actualizan saldo y estado. PIC1 además renueva su QR.
    const reloj = setInterval(() => void traer(), REFRESCO_MS);
    return () => clearInterval(reloj);
  }, [sesion, traer]);

  useEffect(() => {
    if (!pase?.expira_at) return;
    const vence = Date.parse(pase.expira_at);
    const comprobar = () => { if (Date.now() >= vence) { setSvgQR(''); setPase(p => p ? { ...p, estado: 'vencido', token: null } : p); } };
    const id = setInterval(comprobar, 1000); comprobar();
    return () => clearInterval(id);
  }, [pase?.expira_at]);

  async function guardarEnGoogle() {
    if (!puerto.googleWallet || walletOcupado) return;
    const rev = identidad.current;
    setWalletOcupado(true); setWalletMensaje('');
    try { await puerto.googleWallet(); }
    catch (e) { if (rev === identidad.current) setWalletMensaje(e instanceof Error ? e.message : 'No se pudo preparar Google Wallet.'); }
    finally { if (rev === identidad.current) setWalletOcupado(false); }
  }

  async function guardarEnWallet() {
    if (!puerto.appleWallet || walletOcupado) return;
    const rev = identidad.current;
    setWalletOcupado(true); setWalletMensaje('');
    try {
      await puerto.appleWallet();
      if (rev === identidad.current) setWalletMensaje('En iPhone, confirma «Agregar» cuando se abra Wallet. En computadora se descarga un archivo .pkpass.');
    } catch (e) {
      if (rev === identidad.current) setWalletMensaje(e instanceof Error ? e.message : 'No se pudo descargar el pase.');
    } finally { if (rev === identidad.current) setWalletOcupado(false); }
  }

  function salir() {
    identidad.current++;
    sesionVigente.current = null;
    olvidarSesion();
    setSesion(null);
    setPase(null);
    setSvgQR("");
    setProblema(null); setWalletMensaje(''); setWalletOcupado(false);
  }

  /* ── Lo que se pinta ────────────────────────────────────────────────────── */

  if (!sesion) {
    return <PideLiga cargando={cargando} problema={problema} />;
  }

  if (!pase) {
    return (
      <Marco nombre={nombre} alSalir={salir}>
        {problema ? <Problema texto={problema} /> : <Esperando />}
      </Marco>
    );
  }

  const letrero = letreroDe(pase.estado);
  const color = colorDe(pase.estado);

  return (
    <Marco nombre={nombre || pase.nombre || pase.staff_id} alSalir={salir}>
      <section
        className="panem-glass panem-glass--amber"
        style={{ borderTop: `2px solid ${color}` }}
        aria-live="polite"
      >
        <p className="panem-section-title" style={{ color }}>
          {sinRed ? "Tu código guardado" : letrero.titulo}
        </p>
        <p className="panem-help" style={{ marginTop: 4 }}>
          {sinRed ? "El punto de lectura verificará si puedes canjear." : (pase.qr_formato === "PIC2" && pase.estado === "disponible" ? "Muestra el código en la barra o en la entrada." : letrero.accion)}
        </p>

        {svgQR && pase.qr_formato === 'PIC2' && puerto.googleWallet ? <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button className="panem-button" disabled={sinRed || walletOcupado || !pase.google_wallet_disponible} onClick={() => void guardarEnGoogle()}>Agregar a Google Wallet</button>
          {!pase.google_wallet_disponible ? <p className="panem-help">Gerencia todavía debe habilitar Google Wallet.</p> : null}
          {walletMensaje ? <p role="status">{walletMensaje}</p> : null}
        </div> : null}
        {svgQR && pase.qr_formato === 'PIC2' && puerto.appleWallet ? <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button className="panem-button" disabled={sinRed || walletOcupado || !pase.apple_wallet_disponible} onClick={() => void guardarEnWallet()}>{walletOcupado ? 'Preparando tu pase…' : 'Agregar a Apple Wallet'}</button>
          <p className="panem-help" style={{ marginTop: 12 }}>{pase.apple_wallet_disponible ? 'Guárdalo en tu cartera para mostrarlo al llegar. También puedes usar el código de abajo.' : 'Gerencia todavía debe habilitar Apple Wallet. Puedes usar tu código mientras tanto.'}</p>
          {walletMensaje ? <p className="panem-help" role="status" style={{ marginTop: 12 }}>{walletMensaje}</p> : null}
        </div> : null}

        {svgQR ? (
          <div className="mt-6 flex justify-center">
            {/* El respaldo blanco del QR es una decisión del TEMA, no de esta
                pantalla: vive en `--panem-qr-bg`. Un lector necesita el
                contraste máximo entre módulo y fondo y el marfil sobre espresso
                no se lo da, pero el venue que quiera su propio blanco lo cambia
                en su bloque de variables sin tocar este archivo. */}
            <div
              className="p-4"
              // El QR ya viene como SVG del generador; no es entrada de usuario.
              dangerouslySetInnerHTML={{ __html: svgQR }}
              aria-label="Tu código de cortesía. Muéstralo en la barra."
              role="img"
              style={{
                width: "min(72vw, 320px)",
                background: "var(--panem-qr-bg)",
                borderRadius: "var(--panem-radius-field)",
              }}
            />
          </div>
        ) : null}

        {sinRed ? <p role="status" className="panem-help">Sin conexión. Este QR se guardó al consultar tu pase; el punto de lectura comprobará su vigencia y saldo al canjear.</p> : null}
        <dl className="mt-6 grid grid-cols-2 gap-4">
          <Dato rotulo="Bebidas" valor={sinRed ? 'Sin actualizar' : saldoComoTexto(pase)} grande />
          {pase.bolsas ? <Dato rotulo="Entradas" valor={sinRed ? 'Sin actualizar' : `${pase.bolsas.covers.remanente} de ${pase.bolsas.covers.cupo}`} grande /> : null}
          <Dato rotulo="Perfil" valor={pase.perfil ?? "—"} />
          <Dato rotulo="Noche" valor={pase.night_date} />
          <Dato rotulo="Vigencia" valor={pase.expira_at ? new Date(pase.expira_at).toLocaleString("es-MX") : "—"} />
        </dl>

        {pase.cupo > 0 && pase.productos && pase.productos.length > 0 ? (
          <>
            <hr className="panem-rule" />
            <h2 className="panem-meta panem-muted">Qué puedes pedir</h2>
            <ul className="panem-list" style={{ marginTop: 12 }}>
              {pase.productos.map((p) => (
                <li key={p.sku}>
                  <div className="panem-list-row" style={{ minHeight: 48 }}>
                    {p.nombre}
                  </div>
                </li>
              ))}
            </ul>
          </>
        ) : null}
      </section>

      {problema ? <Problema texto={problema} /> : null}

      {/* El aviso del código rotatorio SOLO cuando hay código. Sin la
          condición, un pase revocado —que no pinta QR— seguía prometiendo que
          «el código cambia cada medio minuto», o sea explicando algo que no
          está en la pantalla. Visto al abrir el estado `revocado`. */}
      {svgQR ? (
        <p className="panem-help" style={{ marginTop: 24, textAlign: "center" }}>
          {pase.qr_formato === "PIC2" ? "El mismo código sirve para bebidas y entrada hasta el cierre de tu noche. No lo compartas." : "El código cambia cada medio minuto. No hace falta recargar."}
        </p>
      ) : null}
    </Marco>
  );
}

/* ── Piezas ──────────────────────────────────────────────────────────────── */

/**
 * El marco de la vista del pase.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * AQUÍ NO VA EL AURA DE PÁGINA, Y ES UNA CORRECCIÓN MEDIDA
 * ═══════════════════════════════════════════════════════════════════════════
 * La primera versión le puso `panem-aura` a este contenedor. Abierta en el
 * navegador se vio el problema: sobre un `min-h-screen` el aura se estira a
 * toda la pantalla y el núcleo ámbar queda DEBAJO del texto. El sistema lo
 * advierte con todas sus letras —«no colocar texto pequeño sobre el núcleo»— y
 * el contraste de 15.26:1 que `contrastes.json` mide es marfil sobre la noche,
 * no marfil sobre ámbar.
 *
 * La referencia visual del sistema (`04-mobile.png`, pantalla 2) resuelve
 * exactamente esta vista: el resplandor no es de la página, es de la TARJETA.
 * Por eso el aura vive en `.panem-glass--amber`, que ya la trae medida y por
 * debajo del panel, y esta pieza se queda con el fondo liso.
 *
 * El contenedor `.panem-ui` NO lo pone esta pieza: lo pone quien monta la app,
 * porque tiene que envolver también al `<body>` para que el fondo llegue hasta
 * el borde del teléfono. Ver `pase/src/main.tsx`.
 */
function Marco({
  nombre,
  alSalir,
  children,
}: {
  nombre: string;
  alSalir: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="mx-auto min-h-screen max-w-md px-4 py-6">
      <header className="mb-6 flex items-baseline justify-between gap-4">
        <div>
          <p className="panem-meta panem-muted">TAP by People Intelligence</p>
          <h1 className="panem-section-title" style={{ marginTop: 4 }}>
            {nombre}
          </h1>
        </div>
        {/* «Salir» existe porque el teléfono puede estar prestado. Un pase sin
            forma de cerrarse es el saldo de alguien a la vista del siguiente.
            Va como botón secundario y no como liga: son 48 px de objetivo
            táctil, que es lo que pide el sistema para algo que se toca con el
            pulgar en un cuarto oscuro. */}
        <button
          type="button"
          onClick={alSalir}
          className="panem-button panem-button--secondary"
          style={{ padding: "8px 16px" }}
        >
          Salir
        </button>
      </header>
      {children}
    </div>
  );
}

function PideLiga({
  cargando,
  problema,
}: {
  cargando: boolean;
  problema: string | null;
}) {
  return (
    // EL AURA ES UNA BANDA DE ARRIBA, NO EL FONDO DE LA PÁGINA.
    //
    // Es la pantalla 3 de `04-mobile.png`: el resplandor ocupa la parte alta y
    // el texto vive DEBAJO, sobre la noche, que es donde su contraste está
    // medido. Estirada a toda la pantalla, el aura se come la legibilidad del
    // cuerpo — lo vi así en el navegador antes de acotarla.
    <div className="relative mx-auto flex min-h-screen max-w-md flex-col justify-end pb-16">
      <div
        className="panem-aura pointer-events-none absolute inset-x-0 top-0"
        style={{
          height: "46vh",
          // SE DESVANECE POR ABAJO, o el aura se lee como un RECTÁNGULO.
          // `.panem-aura` recorta con `overflow: hidden` y el gradiente todavía
          // trae color cuando llega al borde: sin la máscara queda una raya
          // horizontal nítida a media pantalla, que es exactamente lo contrario
          // de un resplandor. Es presentación de ESTA pantalla, no del sistema,
          // y por eso va aquí y no en el archivo del tema.
          maskImage: "linear-gradient(to bottom, #000 55%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(to bottom, #000 55%, transparent 100%)",
        }}
        aria-hidden="true"
      />
      {/* El `px-6` vive AQUÍ y no en el contenedor de arriba: un elemento
          absoluto se posiciona contra la caja de relleno, así que con el padding
          en el padre el aura se recortaba 24 px de cada lado y enseñaba sus dos
          cantos verticales. */}
      <div className="relative px-6">
        <p className="panem-meta panem-muted">TAP by People Intelligence</p>
        <h1 className="panem-title" style={{ marginTop: 8 }}>
          Tu pase de cortesías
        </h1>

        <p className="panem-help" style={{ marginTop: 16 }}>
          {cargando
            ? "Abriendo tu pase…"
            : "Para entrar necesitas la liga que te da gerencia. Se abre una sola vez y deja este teléfono listo para volver sin pedir otra."}
        </p>

        {problema ? <Problema texto={problema} /> : null}
      </div>
    </div>
  );
}

function Dato({
  rotulo,
  valor,
  grande,
}: {
  rotulo: string;
  valor: string;
  grande?: boolean;
}) {
  return (
    <div>
      <dt className="panem-meta panem-muted">{rotulo}</dt>
      <dd
        className="panem-numeric"
        style={{
          marginTop: 4,
          fontSize: grande ? 32 : 16,
          fontWeight: 500,
          lineHeight: 1.15,
        }}
      >
        {valor}
      </dd>
    </div>
  );
}

function Esperando() {
  return (
    <p className="panem-help" style={{ padding: 24, textAlign: "center" }}>
      Cargando tu pase…
    </p>
  );
}

function Problema({ texto }: { texto: string }) {
  return (
    <p
      className="panem-error"
      style={{
        marginTop: 16,
        padding: 16,
        border: "1px solid var(--panem-danger)",
        borderRadius: "var(--panem-radius-field)",
        background: "var(--panem-danger-bg)",
      }}
      role="alert"
    >
      {texto}
    </p>
  );
}
